import { Navigate, Route, Routes } from "react-router-dom";
import {
  ANY_PAGE,
  CLIENT_PLATFORM_MARGINS,
  CLIENT_PLATFORM_MARGINS_CHANGELOG,
  CLIENTS_ADMINISTRATION,
  CLIENTS_ADMINISTRATION_BY_ID,
  DASHBOARDS,
  DASHBOARDS_ACTIONS_PERFORMANCE,
  DASHBOARDS_DATA_ALERTS,
  DATA_EXPLORERS,
  DATA_EXPLORERS_ACTIONS_PERFORMANCE,
  DATA_LIBRARY,
  DATA_LIBRARY_DOCS,
  DATA_LIBRARY_LENS,
  DATASETS_CENTRE,
  DATASETS_CENTRE_DETAILS,
  EMPTY,
  FORGOT_PASSWORD,
  HOME,
  LOGIN,
  OBJECTIVES_TRACKER,
  OBJECTIVES_TRACKER_DETAIL,
  PRIVACY,
  PRIVATE_BASE,
  PUBLIC_BASE,
  RESTORE_PASSWORD,
  SECURITY_CENTRE,
  SECURITY_CENTRE_PERMISSIONS_CC_ACCESSLEVEL,
  SECURITY_CENTRE_PERMISSIONS_CC_SECTIONS,
  SECURITY_CENTRE_PERMISSIONS_CONTROLCENTRE,
  SECURITY_CENTRE_PERMISSIONS_DATAAPI,
  SECURITY_CENTRE_ROLES,
  SECURITY_CENTRE_ROLES_BY_ID,
  SECURITY_CENTRE_USERS,
  SECURITY_CENTRE_USERS_BY_ID,
  SETTINGS,
  TACTIC_LINES_PERFORMANCE,
  TERMS,
  TOOLS,
} from "./shared/lib/routesResolver";
import PublicLayout from "./layout/public/PublicLayout";
import LoginContainer from "./components/public/login/LoginContainer";
import PrivateLayout from "./layout/private/PrivateLayout";
import HomeContainer from "./components/private/home/HomeContainer";
import PrivateRoute from "./layout/private/PrivateRoute";
import { LoadingProvider } from "./contexts/loadingContext";
import { Toaster } from "./shared/components/toast/Toaster";
import TermsContainer from "./components/public/terms/TermsContainer";
import PrivacyContainer from "./components/public/privacy/PrivacyContainer";
import ForgotPasswordContainer from "./components/public/password/forgot/ForgotPasswordContainer";
import RestorePasswordContainer from "./components/public/password/restore/RestorePasswordContainer";
import ObjectivesTrackerOverview from "./components/private/sections/tools/objectives-tracker/ObjectivesTrackerOverview";
import { TooltipProvider } from "./shared/components/tooltip/Tooltip";
import DashboardsOverview from "./components/private/sections/dashboards/DashboardsOverview";
import DataExplorersOverview from "./components/private/sections/data-explorers/DataExplorersOverview";
import withTracking from "./AppTracking";
import ToolsOverview from "./components/private/sections/tools/ToolsOverview";
import usePermissions from "./hooks/usePermissions";
import SettingsOverview from "./layout/private/profile/SettingsOverview";
import ActionsPerformanceDataExplorerContainer from "./components/private/sections/data-explorers/actions-performance/ActionsPerformanceDataExplorerContainer";
import ActionsPerformanceDashboardContainer from "./components/private/sections/dashboards/actions-performance/ActionsPerformanceDashboardContainer";
import TacticLinesPerformanceContainer from "./components/private/sections/data-explorers/tactic-lines-performance/TacticLinesPerformanceContainer";
import ClientsAdministrationOverview from "./components/private/sections/tools/clients-administration/ClientsAdministrationOverview";
import ClientDetailContainer from "./components/private/sections/tools/clients-administration/detail/ClientDetailContainer";
import DataAlertsDashboardContainer from "./components/private/sections/dashboards/data-alerts/DataAlertsDashboardContainer";
import InfoPageContainer from "./shared/components/info-pages/InfoPageContainer";
import { INFO_PAGE } from "./shared/components/info-pages/infoPage";
import ObjectivesTrackerDetailContainer from "./components/private/sections/tools/objectives-tracker/detail/ObjectivesTrackerDetailContainer";
import { NavbarItemsProvider } from "./contexts/navbarItemsContext";
import ClientPlatformMarginsOverview from "./components/private/sections/tools/client-platform-margins/ClientPlatformMarginsOverview";
import ClientPlatformMarginsChangelogOverview from "./components/private/sections/tools/client-platform-margins/changelog/ClientPlatformMarginsChangelogOverview";
import DataLibraryOverview from "./components/private/sections/data-explorers/data-library/DataLibraryOverview";
import DataLibraryDocs from "./components/private/sections/data-explorers/data-library/docs/DataLibraryDocs";
import DataLibraryLens from "./components/private/sections/data-explorers/data-library/lens/DataLibraryLens";
import { DataLibraryProvider } from "./contexts/dataLibraryContext";
import DatasetsPage from "./components/private/sections/tools/datasets-centre/DatasetsPage";
import DatasetDetailPage from "./components/private/sections/tools/datasets-centre/dataset-detail/DatasetDetailPage";
import SecurityCentreOverview from "./components/private/sections/tools/security-centre/SecurityCentreOverview";
import UsersOverview from "./components/private/sections/tools/security-centre/user/UsersOverview";
import UserDetail from "./components/private/sections/tools/security-centre/user/detail/UserDetail";
import RolesOverview from "./components/private/sections/tools/security-centre/role/RolesOverview";
import RoleDetail from "./components/private/sections/tools/security-centre/role/detail/RoleDetail";
import DataApiPermissionsOverview from "./components/private/sections/tools/security-centre/permission/dataapi/DataApiPermissionsOverview";
import ControlCentrePermissionsOverview from "./components/private/sections/tools/security-centre/permission/controlcentre/ControlCentrePermissionsOverview";
import CCSectionsPermissionsOverview from "./components/private/sections/tools/security-centre/permission/controlcentre/sections/CCSectionsPermissionsOverview";
import CCAccessLevelPermissionsOverview from "./components/private/sections/tools/security-centre/permission/controlcentre/access-level/CCAccessLevelPermissionsOverview";

const SecureNavigationComponent = ({ children }) => {
  const permissions = usePermissions();
  const hasAccess = permissions.hasAccess();

  return hasAccess ? (
    children
  ) : (
    <InfoPageContainer type={INFO_PAGE.NOT_ALLOWED} />
  );
};

const App = () => (
  <LoadingProvider>
    <TooltipProvider>
      <NavbarItemsProvider>
        <DataLibraryProvider>
          <Toaster />
          <Routes>
            <Route path={PUBLIC_BASE} element={<PublicLayout />}>
              <Route path={PUBLIC_BASE} element={<Navigate to={LOGIN} />} />
              <Route path={LOGIN} element={<LoginContainer />} />
              <Route
                path={FORGOT_PASSWORD}
                element={<ForgotPasswordContainer />}
              />
              <Route
                path={RESTORE_PASSWORD}
                element={<RestorePasswordContainer />}
              />
              <Route path={TERMS} element={<TermsContainer />} />
              <Route path={PRIVACY} element={<PrivacyContainer />} />
            </Route>

            <Route path={PRIVATE_BASE} element={<PrivateRoute />}>
              <Route path={EMPTY} element={<PrivateLayout />}>
                <Route path={EMPTY} element={<Navigate to={HOME} />} />
                <Route path={HOME} element={<HomeContainer />} />
                <Route path={SETTINGS} element={<SettingsOverview />} />
                {/* Tools routing */}
                <Route
                  path={TOOLS}
                  element={
                    <SecureNavigationComponent>
                      <ToolsOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={CLIENTS_ADMINISTRATION}
                  element={
                    <SecureNavigationComponent>
                      <ClientsAdministrationOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={CLIENTS_ADMINISTRATION_BY_ID}
                  element={
                    <SecureNavigationComponent>
                      <ClientDetailContainer />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={OBJECTIVES_TRACKER}
                  element={
                    <SecureNavigationComponent>
                      <ObjectivesTrackerOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={OBJECTIVES_TRACKER_DETAIL}
                  element={
                    <SecureNavigationComponent>
                      <ObjectivesTrackerDetailContainer />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={CLIENT_PLATFORM_MARGINS}
                  element={
                    <SecureNavigationComponent>
                      <ClientPlatformMarginsOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={CLIENT_PLATFORM_MARGINS_CHANGELOG}
                  element={
                    <SecureNavigationComponent>
                      <ClientPlatformMarginsChangelogOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={DATASETS_CENTRE}
                  element={
                    <SecureNavigationComponent>
                      <DatasetsPage />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={DATASETS_CENTRE_DETAILS}
                  element={
                    <SecureNavigationComponent>
                      <DatasetDetailPage />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={SECURITY_CENTRE}
                  element={
                    <SecureNavigationComponent>
                      <SecurityCentreOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={SECURITY_CENTRE_USERS}
                  element={
                    <SecureNavigationComponent>
                      <UsersOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={SECURITY_CENTRE_USERS_BY_ID}
                  element={
                    <SecureNavigationComponent>
                      <UserDetail />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={SECURITY_CENTRE_ROLES}
                  element={
                    <SecureNavigationComponent>
                      <RolesOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={SECURITY_CENTRE_ROLES_BY_ID}
                  element={
                    <SecureNavigationComponent>
                      <RoleDetail />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={SECURITY_CENTRE_PERMISSIONS_DATAAPI}
                  element={
                    <SecureNavigationComponent>
                      <DataApiPermissionsOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={SECURITY_CENTRE_PERMISSIONS_CONTROLCENTRE}
                  element={
                    <SecureNavigationComponent>
                      <ControlCentrePermissionsOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={SECURITY_CENTRE_PERMISSIONS_CC_SECTIONS}
                  element={
                    <SecureNavigationComponent>
                      <CCSectionsPermissionsOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={SECURITY_CENTRE_PERMISSIONS_CC_ACCESSLEVEL}
                  element={
                    <SecureNavigationComponent>
                      <CCAccessLevelPermissionsOverview />
                    </SecureNavigationComponent>
                  }
                />
                {/* Dashboards routing */}
                <Route
                  path={DASHBOARDS}
                  element={
                    <SecureNavigationComponent>
                      <DashboardsOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={DASHBOARDS_ACTIONS_PERFORMANCE}
                  element={
                    <SecureNavigationComponent>
                      <ActionsPerformanceDashboardContainer />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={DASHBOARDS_DATA_ALERTS}
                  element={
                    <SecureNavigationComponent>
                      <DataAlertsDashboardContainer />
                    </SecureNavigationComponent>
                  }
                />
                {/* Data explorers routing */}
                <Route
                  path={DATA_EXPLORERS}
                  element={
                    <SecureNavigationComponent>
                      <DataExplorersOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={DATA_EXPLORERS_ACTIONS_PERFORMANCE}
                  element={
                    <SecureNavigationComponent>
                      <ActionsPerformanceDataExplorerContainer />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={TACTIC_LINES_PERFORMANCE}
                  element={
                    <SecureNavigationComponent>
                      <TacticLinesPerformanceContainer />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={DATA_LIBRARY}
                  element={
                    <SecureNavigationComponent>
                      <DataLibraryOverview />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={DATA_LIBRARY_DOCS}
                  element={
                    <SecureNavigationComponent>
                      <DataLibraryDocs />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={DATA_LIBRARY_LENS}
                  element={
                    <SecureNavigationComponent>
                      <DataLibraryLens />
                    </SecureNavigationComponent>
                  }
                />
                <Route
                  path={ANY_PAGE}
                  element={<InfoPageContainer type={INFO_PAGE.NOT_FOUND} />}
                />
              </Route>
            </Route>
          </Routes>
        </DataLibraryProvider>
      </NavbarItemsProvider>
    </TooltipProvider>
  </LoadingProvider>
);

export default withTracking(App);
